/**
 * Cookie情報の取得・更新を共通定義
 */
import _Vue from 'vue'

/**
 * 型の取り込み
 */
import { AuthBcbt, StudentCookie, AdminCookie, AcademyCookie, BranchCookie } from '@/models/Cookies'

/**
 * 本体定義
 */
export default {
  install(Vue: typeof _Vue): void {
    /**
     * 認証情報
     */
    const authBcbt = {
      get: function (): AuthBcbt | null {
        return Vue.prototype.$cookies.get('authBcbt')
      },
      set: async function (authBcbt: AuthBcbt) {
        await Vue.prototype.$cookies.set(
          'authBcbt',
          authBcbt,
          Function(`return (${process.env.VUE_APP_COOKIE_EXPIRE})`)()
        )
      },
      remove: function () {
        Vue.prototype.$cookies.remove('authBcbt')
      },
    }
    /**
     * 戻り先URL(URIエンコードされた状態で保持する)
     */
    const returnUrl = {
      get: function (): string {
        const cookieUrl = Vue.prototype.$cookies.get('returnUrl')
        if (!cookieUrl) {
          // cookieに戻り先URLが保持されていない場合はデフォルト設定を返す
          return process.env.VUE_APP_ENDPOINT
        }
        // 保持されている場合はURIデコードして返す
        return decodeURIComponent(cookieUrl)
      },
      set: async function (returnUrl: string) {
        // セッションクッキーとして保持する
        await Vue.prototype.$cookies.set('returnUrl', returnUrl, 0)
      },
      remove: function () {
        Vue.prototype.$cookies.remove('returnUrl')
      },
    }
    /**
     * 生徒画面Cookie
     */
    const studentCookie = {
      get: function (): StudentCookie | null {
        return Vue.prototype.$cookies.get('studentCookie')
      },
      set: async function (studentCookie: StudentCookie) {
        await Vue.prototype.$cookies.set(
          'studentCookie',
          studentCookie,
          Function(`return (${process.env.VUE_APP_COOKIE_EXPIRE})`)()
        )
      },
      remove: function () {
        Vue.prototype.$cookies.remove('studentCookie')
      },
    }
    /**
     * システム管理画面Cookie
     */
    const adminCookie = {
      get: function (): AdminCookie | null {
        return Vue.prototype.$cookies.get('adminCookie')
      },
      set: async function (adminCookie: AdminCookie) {
        await Vue.prototype.$cookies.set(
          'adminCookie',
          adminCookie,
          Function(`return (${process.env.VUE_APP_COOKIE_EXPIRE})`)()
        )
      },
      remove: function () {
        Vue.prototype.$cookies.remove('adminCookie')
      },
    }
    /**
     * 塾管理画面Cookie
     */
    const academyCookie = {
      get: function (): AcademyCookie | null {
        return Vue.prototype.$cookies.get('academyCookie')
      },
      set: async function (academyCookie: AcademyCookie) {
        await Vue.prototype.$cookies.set(
          'academyCookie',
          academyCookie,
          Function(`return (${process.env.VUE_APP_COOKIE_EXPIRE})`)()
        )
      },
      remove: function () {
        Vue.prototype.$cookies.remove('academyCookie')
      },
    }
    /**
     * 教室管理画面Cookie
     */
    const branchCookie = {
      get: function (): BranchCookie | null {
        return Vue.prototype.$cookies.get('branchCookie')
      },
      set: async function (branchCookie: BranchCookie) {
        await Vue.prototype.$cookies.set(
          'branchCookie',
          branchCookie,
          Function(`return (${process.env.VUE_APP_COOKIE_EXPIRE})`)()
        )
      },
      remove: function () {
        Vue.prototype.$cookies.remove('branchCookie')
      },
    }
    /**
     * 全Cookieの有効期間延長
     */
    const updateCookieExpire = async function () {
      const tmpAuthBcbt = authBcbt.get()
      const tmpStudentCookie = studentCookie.get()
      const tmpAdminCookie = adminCookie.get()
      const tmpAcademyCookie = academyCookie.get()
      const tmpBranchCookie = branchCookie.get()
      if (tmpAuthBcbt) {
        await authBcbt.set(tmpAuthBcbt)
      }
      if (tmpStudentCookie) {
        await studentCookie.set(tmpStudentCookie)
      }
      if (tmpAdminCookie) {
        await adminCookie.set(tmpAdminCookie)
      }
      if (tmpAcademyCookie) {
        await academyCookie.set(tmpAcademyCookie)
      }
      if (tmpBranchCookie) {
        await branchCookie.set(tmpBranchCookie)
      }
    }

    Vue.prototype.$bcbtCookies = {
      authBcbt,
      returnUrl,
      studentCookie,
      adminCookie,
      academyCookie,
      branchCookie,
      updateCookieExpire,
    }
  },
}

/**
 * this.$bcbtCookiesで呼び出せるようインターフェース定義
 */
declare module 'vue/types/vue' {
  interface Vue {
    $bcbtCookies: {
      authBcbt: {
        get: () => AuthBcbt | null
        set: (authBcbt: AuthBcbt) => Promise<void>
        remove: () => void
      }
      returnUrl: {
        get: () => string
        set: (token: string) => Promise<void>
        remove: () => void
      }
      studentCookie: {
        get: () => StudentCookie | null
        set: (studentCookie: StudentCookie) => Promise<void>
        remove: () => void
      }
      adminCookie: {
        get: () => AdminCookie | null
        set: (adminCookie: AdminCookie) => Promise<void>
        remove: () => void
      }
      academyCookie: {
        get: () => AcademyCookie | null
        set: (academyCookie: AcademyCookie) => Promise<void>
        remove: () => void
      }
      branchCookie: {
        get: () => BranchCookie | null
        set: (branchCookie: BranchCookie) => Promise<void>
        remove: () => void
      }
      /**
       * 全Cookieの有効期間延長
       */
      updateCookieExpire: () => Promise<void>
    }
  }
}
